import { useState, useCallback, useRef } from 'react'
import { Utils as utils } from '@client/utility'
import { GetUserUUID } from '@components'
import { usePyrenzAlert } from '@renderer/provider'

interface PersonaCard {
  id: string
  persona_name: string
  persona_description: string
  is_selected?: boolean
  persona_profile?: string | null
  user_uuid: string
}

export const usePersonaAPI = () => {
  const [personaData, setPersonaData] = useState<PersonaCard[]>([])
  const [loading, setLoading] = useState(false)
  const [userUuid, setUserUuid] = useState<string | null>(null)

  const showAlert = usePyrenzAlert()
  const fetchedUuidRef = useRef(false)

  const fetchUserUuid = useCallback(async () => {
    if (fetchedUuidRef.current) return
    fetchedUuidRef.current = true

    try {
      const uuid = await GetUserUUID()
      if (!uuid) {
        setUserUuid(null)
        return
      }
      setUserUuid(uuid)
    } catch {
      showAlert('Failed to load user session.', 'alert')
    }
  }, [showAlert])

  const fetchPersona = useCallback(async () => {
    if (!userUuid) return
    setLoading(true)

    try {
      const result = await utils.db.select<PersonaCard>({
        tables: 'personas',
        columns:
          'id, persona_name, persona_description, persona_profile, is_selected, user_uuid',
        match: { user_uuid: userUuid },
      })

      setPersonaData(result?.data ?? [])
    } catch {
      showAlert('Failed to fetch persona data.', 'alert')
    } finally {
      setLoading(false)
    }
  }, [userUuid, showAlert])

  const handleCreatePersona = useCallback(
    async (
      name: string,
      description: string,
      profile: string,
      setName: (v: string) => void,
      setDescription: (v: string) => void,
      setProfile: (v: string | null) => void,
      setModalOpen: (v: boolean) => void
    ) => {
      if (!userUuid || !name.trim()) return

      try {
        await utils.db.insert<PersonaCard>({
          tables: 'personas',
          data: {
            persona_name: name,
            persona_description: description,
            persona_profile: profile || null,
            user_uuid: userUuid,
            is_selected: false,
          },
        })

        await fetchPersona()

        setName('')
        setDescription('')
        setProfile(null)
        setModalOpen(false)
      } catch {
        showAlert('Failed to create persona.', 'alert')
      }
    },
    [userUuid, fetchPersona, showAlert]
  )

  const handleEditPersona = useCallback(
    async (
      id: string,
      name: string,
      description: string,
      profile: string,
      setName: (v: string) => void,
      setDescription: (v: string) => void,
      setProfile: (v: string | null) => void,
      setModalOpen: (v: boolean) => void,
      setEditingPersona: (v: null) => void
    ) => {
      try {
        await utils.db.update<PersonaCard>({
          tables: 'personas',
          values: {
            persona_name: name,
            persona_description: description,
            persona_profile: profile || null,
          },
          match: { id },
        })

        await fetchPersona()

        setName('')
        setDescription('')
        setProfile(null)
        setEditingPersona(null)
        setModalOpen(false)
      } catch {
        showAlert('Failed to update persona.', 'alert')
      }
    },
    [fetchPersona, showAlert]
  )

 const handleDeletePersona = useCallback(
  async (id: string) => {
    try {
      const { error } = await utils.db.client
        .from('personas')
        .delete()
        .match({ id })

      if (error) throw error

      await fetchPersona()
    } catch {
      showAlert('Failed to delete persona.', 'alert')
    }
  },
  [fetchPersona, showAlert]
)
  const handleSelectPersona = useCallback(
    async (id: string) => {
      if (!userUuid) return

      try {
        await utils.db.update<PersonaCard>({
          tables: 'personas',
          values: { is_selected: false },
          match: { user_uuid: userUuid },
        })

        await utils.db.update<PersonaCard>({
          tables: 'personas',
          values: { is_selected: true },
          match: { id },
        })

        await fetchPersona()
      } catch {
        showAlert('Failed to select persona.', 'alert')
      }
    },
    [userUuid, fetchPersona, showAlert]
  )

  return {
    personaData,
    loading,
    userUuid,
    fetchUserUuid,
    fetchPersona,
    handleCreatePersona,
    handleEditPersona,
    handleDeletePersona,
    handleSelectPersona,
  }
}